import React, { useState } from "react";
import axios from "axios";
import {
  Loader2,
  Download,
} from "lucide-react";
import {
  API,
  REPORTS,
} from "./shared";

export function ReportsTab({ authHeader }) {
  const [report, setReport] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ frm: "", to: "" });

  const run = async (key) => {
    setReport(key);
    setLoading(true);
    setData(null);
    try {
      const res = await axios.get(`${API}/admin/reports/${key}`, {
        headers: authHeader(),
        params: { frm: range.frm, to: range.to },
      });
      setData(res.data);
    } catch {} finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/reports/${report}`, {
        headers: authHeader(),
        params: { frm: range.frm, to: range.to, format: "csv" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `soarlife-${report}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="reports-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Reporting Center</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Seven live reports across revenue, bookings, clients, clinics, interpreters, education, and wellness.
          Filter by date range and download any report as CSV.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            data-testid={`report-${r.key}`}
            onClick={() => run(r.key)}
            className={`text-left bg-white rounded-3xl p-4 border-2 transition-all ${
              report === r.key ? "border-[#3F5043] shadow-lg" : "border-[#BCB3A6]/40 hover:border-[#899480]"
            }`}
          >
            <div className="font-serif text-lg text-[#3F5043] mb-1">{r.label}</div>
            <div className="text-[11px] text-[#3F5043]/60 leading-relaxed">{r.desc}</div>
          </button>
        ))}
      </div>

      {report && (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5" data-testid="report-results">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="font-serif text-xl text-[#3F5043] mr-auto">
              {REPORTS.find((r) => r.key === report)?.label}
            </div>
            <input type="date" data-testid="report-from" value={range.frm}
              onChange={(e) => setRange({ ...range, frm: e.target.value })} className={inputCls} />
            <span className="text-[#3F5043]/50 text-sm">to</span>
            <input type="date" data-testid="report-to" value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })} className={inputCls} />
            <button onClick={() => run(report)} data-testid="report-refresh"
              className="rounded-full px-4 py-2 bg-[#EDEDEE] text-[#3F5043] text-xs font-medium hover:bg-[#899480] hover:text-white transition-colors">
              Apply
            </button>
            <button onClick={downloadCsv} data-testid="report-csv"
              className="rounded-full px-4 py-2 bg-[#3F5043] text-[#F8F7F3] text-xs font-medium hover:bg-[#875F45] transition-colors inline-flex items-center gap-1">
              <Download size={13} /> CSV
            </button>
          </div>

          {loading && (
            <div className="text-center py-10 text-[#899480]">
              <Loader2 className="animate-spin inline" size={22} />
            </div>
          )}

          {data && !loading && (
            <>
              <div className="flex flex-wrap gap-2 mb-4" data-testid="report-summary">
                {Object.entries(data.summary).map(([k, v]) => (
                  <span key={k} className="text-[11px] bg-[#F8F7F3] border border-[#BCB3A6]/40 rounded-full px-3 py-1.5 text-[#3F5043]">
                    {k.replace(/_/g, " ")}: <strong>{typeof v === "object" ? JSON.stringify(v) : v}</strong>
                  </span>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#EDEDEE] text-left text-[10px] uppercase tracking-[0.18em] text-[#899480]">
                      {data.columns.map((c) => (
                        <th key={c} className="px-4 py-3 whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody data-testid="report-table">
                    {data.rows.length === 0 && (
                      <tr><td colSpan={data.columns.length} className="px-4 py-8 text-center text-[#3F5043]/60">
                        No data for this range.
                      </td></tr>
                    )}
                    {data.rows.map((row, i) => (
                      <tr key={i} data-testid={`report-row-${i}`} className="border-t border-[#BCB3A6]/20">
                        {row.map((cell, j) => (
                          <td key={j} className={`px-4 py-2.5 ${cell === "AT RISK" ? "text-red-700 font-semibold" : "text-[#3F5043]/80"}`}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
